import { sortContentRowsByOrder } from './scheduleContentMatcher';
import {
  deleteProgramFixedContentRowsByIds,
  getProgramFixedContentRows,
  listProgramFixedContentRowIds,
  upsertProgramFixedContentRows,
} from '../services/programFixedContentService';
import {
  getProgramScopedContentRows,
  saveProgramScopedContentRowsDiff,
  updateProgramScopedContentRowFieldsById,
  type ProgramScopedContentRowFieldPatch,
} from '../services/programScopedContentService';
import { fetchProgramContentWorksheetPresence } from '../services/programContentWorksheetService';
import {
  getProgramProgressCellsForNaturalKeys,
  upsertProgramProgressCells,
} from '../services/programProgressService';
import type { ProgramMutationNotifyOptions } from '../services/programFixedContentService';
import type {
  ProgramFixedContentRow,
  ProgramFixedContentRowUpsert,
  ProgramProgressCell,
  ProgramProgressCellUpsert,
  ProgramScopeType,
  ProgramScopedContentRow,
  ProgramScopedContentRowUpsert,
} from '../types/domain';
import { buildTrackSyncFanoutScopedUpsert } from './trackSyncScopedContent';
import {
  getProgramWorkspaceScopeEntries,
  pickProgramWorkspacePatchRowsForSave,
  removeProgramWorkspaceScopeEntry,
  type ProgramWorkspaceScopeEntry,
} from './programEditWorkspace';
import { buildAnnualCellKey, parseAnnualCellKey } from './annualTimetable';
import { filterOutSkipFixedTablePersistRows } from './programWorkspaceBaselineDigest';
import { notifySchoolProgramMutation } from './schoolDataCacheVersion';
import { mergeProgramWorkspaceUpsertsIntoBaselineRows } from './programWorkspaceDisplayMerge';
import {
  emitProgramProgressSaved,
  type ProgramProgressUiRefreshMode,
} from './programProgressSavedEvents';
import {
  normalizeFixedContentRowForStorage,
  normalizeScopedContentRowForStorage,
  textForStorage,
} from './programContentNullNormalize';
import {
  readProgramLearningContentFields,
  writeProgramLearningContentFields,
} from './programLearningContentFields';
import { setProgramFixedRowsQueryData, setProgramScopedContentRowsQueryData } from '../query/cacheUpdates';
import { replaceScopeRowsInAnnualGradeTermCaches } from './programAttachmentRowCacheMerge';
import {
  loadProgramFixedContentRowsCache,
  mergeProgramFixedContentRowsCacheRows,
  saveProgramFixedContentRowsCache,
} from './programFixedContentRowsCache';
import {
  loadProgramScopedContentRowsCache,
  saveProgramScopedContentRowsCache,
} from './programScopedContentRowsCache';

/** 통합 저장 루프 안에서는 workspaces 갱신을 배치 끝으로 미룸 */
const DEFER_SCHOOL_NOTIFY: ProgramMutationNotifyOptions = { deferSchoolVersionNotify: true };

function normalizeUpsertIds<T extends { id?: string }>(rows: T[]): T[] {
  return rows.map((row) => {
    const id = row.id?.trim() ?? '';
    if (!id || id.startsWith('virtual-') || id.startsWith('extra:') || id.startsWith('orphan:') || id.startsWith('temp-')) {
      const { id: _drop, ...rest } = row;
      return rest as T;
    }
    return { ...row, id };
  });
}

type ScopedLike = ProgramFixedContentRowUpsert | ProgramScopedContentRow;

function mapScopedLikeToScopedUpsert(
  row: ScopedLike,
  entry: ProgramWorkspaceScopeEntry,
  scopeType: ProgramScopeType,
  orderIndex: number,
  opts: { trackScoped: boolean }
): ProgramScopedContentRowUpsert {
  const raw: ProgramScopedContentRowUpsert = {
    id: row.id,
    workspace_id: row.workspace_id,
    grade_id: row.grade_id,
    academic_year: row.academic_year,
    term_number: row.term_number,
    scope_type: scopeType,
    scope_key: entry.scopeKey,
    /** 편제·프로젝트: 표 위치가 곧 순서. 셀 키는 쓰지 않는다(일정과는 위치로 짝지음) */
    order_index: orderIndex,
    source_annual_cell_key: null,
    timetable_text: opts.trackScoped ? null : ('timetable_text' in row ? row.timetable_text ?? null : null),
    unit_text: row.unit_text ?? null,
    learning_text: row.learning_text ?? null,
    learning_note: row.learning_note ?? '',
    presentation_ref_id: row.presentation_ref_id ?? null,
    worksheet_ref_id: row.worksheet_ref_id ?? null,
    page_text: row.page_text ?? null,
    assistant_page_text: row.assistant_page_text ?? null,
    current_period_text: row.current_period_text ?? null,
    total_period_text: row.total_period_text ?? null,
    material_text: row.material_text ?? null,
    record_cross_curricular_text: row.record_cross_curricular_text ?? null,
    record_memo_text: row.record_memo_text ?? null,
    record_evaluation_text: row.record_evaluation_text ?? null,
    /** 수업기록은 스코프 행 본문으로 관리(스케줄 유무와 무관). */
    record_class_text: row.record_class_text ?? null,
    record_member_group_id: row.record_member_group_id ?? null,
    bg_color_by_field: row.bg_color_by_field ?? {},
  };
  return normalizeScopedContentRowForStorage(raw);
}

function mapFixedLikeToFixedUpsert(row: ProgramFixedContentRow | ProgramFixedContentRowUpsert): ProgramFixedContentRowUpsert {
  const raw: ProgramFixedContentRowUpsert = {
    id: row.id,
    workspace_id: row.workspace_id,
    grade_id: row.grade_id,
    academic_year: row.academic_year,
    term_number: row.term_number,
    order_index: row.order_index,
    source_annual_cell_key: row.source_annual_cell_key ?? null,
    event_name: 'event_name' in row ? row.event_name : null,
    timetable_text: row.timetable_text ?? null,
    unit_text: row.unit_text ?? null,
    learning_text: row.learning_text ?? null,
    learning_note: row.learning_note ?? '',
    presentation_ref_id: row.presentation_ref_id ?? null,
    worksheet_ref_id: row.worksheet_ref_id ?? null,
    page_text: row.page_text ?? null,
    assistant_page_text: row.assistant_page_text ?? null,
    current_period_text: row.current_period_text ?? null,
    total_period_text: row.total_period_text ?? null,
    material_text: row.material_text ?? null,
    record_cross_curricular_text: row.record_cross_curricular_text ?? null,
    record_memo_text: row.record_memo_text ?? null,
    record_evaluation_text: row.record_evaluation_text ?? null,
    record_class_text: row.record_class_text ?? null,
    record_member_group_id: row.record_member_group_id ?? null,
    bg_color_by_field: row.bg_color_by_field ?? {},
  };
  return normalizeFixedContentRowForStorage(raw);
}

async function sanitizeScopedWorksheetRefs(
  workspaceId: string,
  rows: ProgramScopedContentRowUpsert[]
): Promise<ProgramScopedContentRowUpsert[]> {
  const sid = workspaceId.trim();
  if (!sid || !rows.length) return rows;
  const refIds = Array.from(
    new Set(
      rows
        .map((row) => readProgramLearningContentFields(row).worksheetRefId ?? '')
        .map((id) => id.trim())
        .filter(Boolean)
    )
  );
  if (!refIds.length) return rows;
  const presence = await fetchProgramContentWorksheetPresence(sid, refIds);
  return rows.map((row) => {
    const fields = readProgramLearningContentFields(row);
    const refId = (fields.worksheetRefId ?? '').trim();
    if (!refId || presence.has(refId)) return row;
    const cleared = writeProgramLearningContentFields({ ...fields, worksheetRefId: null });
    return { ...row, ...cleared };
  });
}

async function saveFixedScope(entry: ProgramWorkspaceScopeEntry): Promise<ProgramFixedContentRow[]> {
  const rowsForSave = pickProgramWorkspacePatchRowsForSave(entry);
  const normalized = normalizeUpsertIds<ProgramFixedContentRowUpsert>(rowsForSave);
  let mergedFixed: ProgramFixedContentRowUpsert[] = normalized;
  if (entry.persistKind === 'patch') {
    const existing = await getProgramFixedContentRows(
      entry.workspaceId,
      entry.academicYear,
      entry.termNumber,
      entry.gradeId,
      { forceRefresh: true }
    );
    const sorted = [...existing].sort((a, b) => a.order_index - b.order_index);
    mergedFixed = mergeProgramWorkspaceUpsertsIntoBaselineRows(sorted, normalized).map(mapFixedLikeToFixedUpsert);
  }
  const toPersist = filterOutSkipFixedTablePersistRows(mergedFixed);
  if (!toPersist.length) {
    return [];
  }
  const savedRows = await upsertProgramFixedContentRows(toPersist, DEFER_SCHOOL_NOTIFY);
  /** replace만 고아 행 정리 — patch는 변경 행만 올려 keepIds가 부분이라 삭제가 위험 */
  if (entry.persistKind === 'replace') {
    const existingIds = await listProgramFixedContentRowIds(
      entry.workspaceId,
      entry.academicYear,
      entry.termNumber,
      entry.gradeId
    );
    const keepIds = new Set(savedRows.map((row) => row.id).filter((id): id is string => !!id));
    const removeIds = existingIds.filter((id) => !keepIds.has(id));
    if (removeIds.length) {
      await deleteProgramFixedContentRowsByIds(removeIds, DEFER_SCHOOL_NOTIFY);
    }
  }
  return savedRows;
}

const SCOPED_FIELD_UPDATE_KEYS = [
  'timetable_text',
  'unit_text',
  'learning_text',
  'learning_note',
  'presentation_ref_id',
  'worksheet_ref_id',
  'page_text',
  'assistant_page_text',
  'current_period_text',
  'total_period_text',
  'material_text',
  'record_cross_curricular_text',
  'record_memo_text',
  'record_evaluation_text',
  'record_class_text',
  'record_member_group_id',
  'bg_color_by_field',
] as const;

/**
 * 칸 글자만 바뀐 저장인지 판단한다. 행 수·순서(id 배열)가 기준본과 같고 바뀐 행이 모두 기존 id면
 * `{ id, fields }[]`(바뀐 칸만)를, 구조가 바뀌었으면 null을 돌려준다.
 */
export function planScopedFieldOnlyUpdates(
  baselineSorted: readonly ProgramScopedContentRow[],
  snapshot: readonly ProgramScopedContentRowUpsert[]
): Array<{ id: string; fields: ProgramScopedContentRowFieldPatch }> | null {
  if (baselineSorted.length !== snapshot.length) return null;
  const out: Array<{ id: string; fields: ProgramScopedContentRowFieldPatch }> = [];
  for (let i = 0; i < snapshot.length; i += 1) {
    const base = baselineSorted[i];
    const next = snapshot[i];
    if (!base?.id || !next?.id || base.id !== next.id) return null;
    const baseNorm = normalizeScopedContentRowForStorage(base as unknown as ProgramScopedContentRowUpsert) as unknown as Record<
      string,
      unknown
    >;
    const nextNorm = normalizeScopedContentRowForStorage(next) as unknown as Record<string, unknown>;
    const fields: Record<string, unknown> = {};
    SCOPED_FIELD_UPDATE_KEYS.forEach((key) => {
      const a = baseNorm[key];
      const b = nextNorm[key];
      const same =
        key === 'bg_color_by_field'
          ? stableJson(a ?? {}) === stableJson(b ?? {})
          : String(a ?? '') === String(b ?? '');
      if (!same) fields[key] = b ?? (key === 'bg_color_by_field' ? {} : null);
    });
    if (Object.keys(fields).length) out.push({ id: next.id, fields: fields as ProgramScopedContentRowFieldPatch });
  }
  return out;
}

function stableJson(value: unknown): string {
  if (!value || typeof value !== 'object') return JSON.stringify(value ?? null);
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined && v !== null && String(v) !== '')
    .sort(([x], [y]) => x.localeCompare(y));
  return JSON.stringify(entries);
}

/**
 * 편제·프로젝트 한 스코프 저장(위치 + id 기준).
 * - 칸 글자만 바뀐 patch: 바뀐 행만 id로 고친다(행마다 쓰기 1번, 순서 목록 안 보냄).
 * - 추가·삭제·이동·붙여넣기 등: 최신 서버 목록을 기준으로 화면 순서 전체를 DB 함수 한 번으로 반영.
 * 반환: 저장 후 스코프 전체 행(순서대로).
 */
async function persistScopedWorkspaceEntry(
  entry: ProgramWorkspaceScopeEntry,
  scopeType: ProgramScopeType,
  opts: { trackScoped: boolean }
): Promise<ProgramScopedContentRow[]> {
  const workspaceRows = normalizeUpsertIds<ProgramFixedContentRowUpsert>(pickProgramWorkspacePatchRowsForSave(entry));
  const baseline = sortContentRowsByOrder(
    await getProgramScopedContentRows(
      entry.workspaceId,
      entry.academicYear,
      entry.termNumber,
      entry.gradeId,
      scopeType,
      entry.scopeKey,
      { forceRefresh: true }
    )
  );
  const isPatch = entry.persistKind !== 'replace';
  const sourceRows: ScopedLike[] = isPatch
    ? mergeProgramWorkspaceUpsertsIntoBaselineRows(baseline, workspaceRows, { identity: 'id-position' })
    : workspaceRows;
  const snapshot = sourceRows.map((row, position) =>
    mapScopedLikeToScopedUpsert(row, entry, scopeType, position, opts)
  );
  const snapshotSanitized = await sanitizeScopedWorksheetRefs(entry.workspaceId, snapshot);

  if (isPatch) {
    const byIdChanges = planScopedFieldOnlyUpdates(baseline, snapshotSanitized);
    if (byIdChanges) {
      if (!byIdChanges.length) return baseline;
      const savedById = new Map<string, ProgramScopedContentRow>();
      for (const change of byIdChanges) {
        const saved = await updateProgramScopedContentRowFieldsById(change.id, change.fields, DEFER_SCHOOL_NOTIFY);
        if (saved) savedById.set(saved.id, saved);
      }
      if (savedById.size === byIdChanges.length) {
        return baseline.map((row) => savedById.get(row.id) ?? row);
      }
      /** 다른 기기에서 지워진 행이 있으면 위치 기준 전체 저장으로 마무리 */
    }
  }

  return saveProgramScopedContentRowsDiff(
    {
      workspaceId: entry.workspaceId,
      academicYear: entry.academicYear,
      termNumber: entry.termNumber,
      gradeId: entry.gradeId,
      scopeType,
      scopeKey: entry.scopeKey,
      baselineRows: baseline,
      snapshotRows: snapshotSanitized,
    },
    DEFER_SCHOOL_NOTIFY
  );
}

async function saveScopedScope(entry: ProgramWorkspaceScopeEntry): Promise<ProgramScopedContentRow[]> {
  return persistScopedWorkspaceEntry(entry, entry.scopeType as ProgramScopeType, { trackScoped: false });
}

function buildProgramCellMap(rows: ProgramProgressCell[]): Record<string, ProgramProgressCell> {
  return rows.reduce<Record<string, ProgramProgressCell>>((acc, row) => {
    acc[buildAnnualCellKey(row.grade_id, row.monday_iso, row.weekday_key, row.slot_index)] = row;
    return acc;
  }, {});
}

/**
 * 동기화 편제: 대표 스코프의 **전체 행(순서대로)**을 다른 시간표 alias 스코프에 같은 순서로 맞춘다.
 * i번째 대표 행 ↔ i번째 미러 행. 수업기록·명단은 미러 행 것을 유지.
 */
async function fanoutSavedTrackScopedRowsToSyncAliases(
  entry: ProgramWorkspaceScopeEntry,
  fullScopeRows: ProgramScopedContentRow[],
  opt: ProgramMutationNotifyOptions
): Promise<void> {
  const keys = entry.trackSyncFanoutScopeKeys;
  if (!keys?.length) return;
  const sourceKey = entry.scopeKey;
  const sortedSrc = sortContentRowsByOrder(fullScopeRows);
  for (const targetKey of keys) {
    if (targetKey === sourceKey) continue;
    const existingSorted = sortContentRowsByOrder(
      await getProgramScopedContentRows(
        entry.workspaceId,
        entry.academicYear,
        entry.termNumber,
        entry.gradeId,
        'track',
        targetKey,
        { forceRefresh: true }
      )
    );
    const snapshot: ProgramScopedContentRowUpsert[] = sortedSrc.map((src, position) =>
      normalizeScopedContentRowForStorage(
        buildTrackSyncFanoutScopedUpsert(src, targetKey, position, null, existingSorted[position] ?? null)
      )
    );
    const snapshotSanitized = await sanitizeScopedWorksheetRefs(entry.workspaceId, snapshot);
    await saveProgramScopedContentRowsDiff(
      {
        workspaceId: entry.workspaceId,
        academicYear: entry.academicYear,
        termNumber: entry.termNumber,
        gradeId: entry.gradeId,
        scopeType: 'track',
        scopeKey: targetKey,
        baselineRows: existingSorted,
        snapshotRows: snapshotSanitized,
        skipRefFork: true,
      },
      opt
    );
  }
}

async function upsertTrackProgramProgressCellsForWorkspaceRows(
  entry: ProgramWorkspaceScopeEntry,
  normalizedRows: ProgramFixedContentRowUpsert[],
  resolveAnnualKey: (row: ProgramFixedContentRowUpsert) => string,
  opt: ProgramMutationNotifyOptions
): Promise<void> {
  const naturalKeys: Array<{
    gradeId: string;
    mondayIso: string;
    weekdayKey: string;
    slotIndex: number;
  }> = [];
  normalizedRows.forEach((row) => {
    const annualKey = resolveAnnualKey(row).trim();
    if (!annualKey) return;
    const parsed = parseAnnualCellKey(annualKey);
    if (!parsed) return;
    naturalKeys.push({
      gradeId: parsed.gradeId,
      mondayIso: parsed.mondayIso,
      weekdayKey: parsed.weekdayKey,
      slotIndex: parsed.slotIndex,
    });
  });
  if (!naturalKeys.length) return;
  const existingProgramCells = await getProgramProgressCellsForNaturalKeys(
    entry.workspaceId,
    entry.academicYear,
    entry.termNumber,
    entry.gradeId,
    naturalKeys
  );
  const existingProgramCellMap = buildProgramCellMap(existingProgramCells);
  const programCellRows: ProgramProgressCellUpsert[] = [];
  normalizedRows.forEach((row) => {
    const annualKey = resolveAnnualKey(row).trim();
    if (!annualKey) return;
    const parsed = parseAnnualCellKey(annualKey);
    if (!parsed) return;
    const cellKey = buildAnnualCellKey(parsed.gradeId, parsed.mondayIso, parsed.weekdayKey, parsed.slotIndex);
    const existingCell = existingProgramCellMap[cellKey];
    const nextBg = { ...(existingCell?.bg_color_by_field ?? {}) };
    programCellRows.push({
      id: existingCell?.id,
      workspace_id: entry.workspaceId,
      grade_id: parsed.gradeId,
      academic_year: entry.academicYear,
      term_number: entry.termNumber,
      monday_iso: parsed.mondayIso,
      weekday_key: parsed.weekdayKey,
      slot_index: parsed.slotIndex,
      timetable_text: textForStorage(existingCell?.timetable_text),
      unit_text: textForStorage(existingCell?.unit_text),
      learning_text: textForStorage(existingCell?.learning_text),
      page_text: textForStorage(existingCell?.page_text),
      assistant_page_text: textForStorage(existingCell?.assistant_page_text),
      current_period_text: textForStorage(existingCell?.current_period_text),
      total_period_text: textForStorage(existingCell?.total_period_text),
      material_text: textForStorage(existingCell?.material_text),
      record_cross_curricular_text: textForStorage(existingCell?.record_cross_curricular_text),
      record_memo_text: textForStorage(existingCell?.record_memo_text),
      record_evaluation_text: textForStorage(existingCell?.record_evaluation_text),
      record_class_text: textForStorage(existingCell?.record_class_text),
      record_member_group_id: existingCell?.record_member_group_id ?? null,
      bg_color_by_field: nextBg,
    });
  });
  if (programCellRows.length) {
    await upsertProgramProgressCells(programCellRows, opt);
  }
}

async function saveTrackScope(entry: ProgramWorkspaceScopeEntry): Promise<ProgramScopedContentRow[]> {
  const fullScopeRows = await persistScopedWorkspaceEntry(entry, 'track', { trackScoped: true });
  await fanoutSavedTrackScopedRowsToSyncAliases(entry, fullScopeRows, DEFER_SCHOOL_NOTIFY);
  return fullScopeRows;
}

function applyPatchSavedFixedRowsToCaches(
  entry: ProgramWorkspaceScopeEntry,
  savedRows: ProgramFixedContentRow[]
): boolean {
  if (!savedRows.length) return true;
  const cached = loadProgramFixedContentRowsCache(
    entry.workspaceId,
    entry.academicYear,
    entry.termNumber,
    entry.gradeId
  );
  if (!cached.found) return false;
  const merged = mergeProgramFixedContentRowsCacheRows(cached.rows, savedRows);
  saveProgramFixedContentRowsCache(
    entry.workspaceId,
    entry.academicYear,
    entry.termNumber,
    entry.gradeId,
    merged
  );
  setProgramFixedRowsQueryData(
    entry.workspaceId,
    entry.academicYear,
    entry.termNumber,
    entry.gradeId,
    merged
  );
  return true;
}

function applyPatchSavedScopedRowsToCaches(
  entry: ProgramWorkspaceScopeEntry,
  scopeType: ProgramScopeType,
  savedRows: ProgramScopedContentRow[]
): boolean {
  if (!savedRows.length) return true;
  const cached = loadProgramScopedContentRowsCache(
    entry.workspaceId,
    entry.academicYear,
    entry.termNumber,
    entry.gradeId,
    scopeType,
    entry.scopeKey
  );
  /**
   * savedRows는 저장 직후 서버 기준 "이 스코프 전체 행"이다. 예전처럼 옛 캐시에 id로 덧붙이면
   * 지워졌거나 id가 바뀐 옛 행(예: 이전 학습내용)이 남아 위치 짝맞춤이 어긋나므로 통째로 쓴다.
   */
  const merged = sortContentRowsByOrder(savedRows);
  saveProgramScopedContentRowsCache(
    entry.workspaceId,
    entry.academicYear,
    entry.termNumber,
    entry.gradeId,
    scopeType,
    entry.scopeKey,
    merged
  );
  setProgramScopedContentRowsQueryData(
    entry.workspaceId,
    entry.academicYear,
    entry.termNumber,
    entry.gradeId,
    scopeType,
    entry.scopeKey,
    merged
  );
  /** 저장 결과 = 이 스코프 전체 행 → 연간진도표 캐시도 스코프째 교체(지워진·id 바뀐 옛 행 제거) */
  replaceScopeRowsInAnnualGradeTermCaches(
    {
      workspaceId: entry.workspaceId,
      academicYear: entry.academicYear,
      termNumber: entry.termNumber,
      gradeId: entry.gradeId,
      scopeType,
      scopeKey: entry.scopeKey,
    },
    merged
  );
  return cached.found;
}

/** replace 저장본을 LS·RQ에 그대로 써서, dirty 제거 직후 표가 빈 캐시를 보지 않게 한다. */
function applyReplaceSavedScopedRowsToCaches(
  entry: ProgramWorkspaceScopeEntry,
  scopeType: ProgramScopeType,
  savedRows: ProgramScopedContentRow[]
): void {
  saveProgramScopedContentRowsCache(
    entry.workspaceId,
    entry.academicYear,
    entry.termNumber,
    entry.gradeId,
    scopeType,
    entry.scopeKey,
    savedRows
  );
  setProgramScopedContentRowsQueryData(
    entry.workspaceId,
    entry.academicYear,
    entry.termNumber,
    entry.gradeId,
    scopeType,
    entry.scopeKey,
    savedRows
  );
  replaceScopeRowsInAnnualGradeTermCaches(
    {
      workspaceId: entry.workspaceId,
      academicYear: entry.academicYear,
      termNumber: entry.termNumber,
      gradeId: entry.gradeId,
      scopeType,
      scopeKey: entry.scopeKey,
    },
    savedRows
  );
}

function applyReplaceSavedFixedRowsToCaches(
  entry: ProgramWorkspaceScopeEntry,
  savedRows: ProgramFixedContentRow[]
): void {
  saveProgramFixedContentRowsCache(
    entry.workspaceId,
    entry.academicYear,
    entry.termNumber,
    entry.gradeId,
    savedRows
  );
  setProgramFixedRowsQueryData(
    entry.workspaceId,
    entry.academicYear,
    entry.termNumber,
    entry.gradeId,
    savedRows
  );
}

export type PersistProgramWorkspaceResult = {
  savedCount: number;
  /** patch·replace(단일 스코프)는 cache, sync fan-out·캐시 병합 실패 등은 full */
  uiRefreshMode: ProgramProgressUiRefreshMode;
};

async function persistProgramWorkspaceEntries(entries: ProgramWorkspaceScopeEntry[]): Promise<PersistProgramWorkspaceResult> {
  if (!entries.length) return { savedCount: 0, uiRefreshMode: 'cache' };
  const workspaceId = entries[0].workspaceId;
  let savedCount = 0;
  let needsFullUiRefresh = false;
  const touchedGradeTerms = new Map<
    string,
    { workspaceId: string; academicYear: number; termNumber: 1 | 2; gradeId: string }
  >();
  for (const entry of entries) {
    const isPatch = entry.persistKind !== 'replace';
    const hasSyncFanout =
      entry.scopeType === 'track' &&
      !!entry.trackSyncFanoutScopeKeys?.some((k) => k !== entry.scopeKey);
    if (hasSyncFanout) needsFullUiRefresh = true;

    if (entry.scopeType === 'fixed') {
      const savedFixed = await saveFixedScope(entry);
      if (isPatch && !hasSyncFanout) {
        if (!applyPatchSavedFixedRowsToCaches(entry, savedFixed)) needsFullUiRefresh = true;
      } else {
        applyReplaceSavedFixedRowsToCaches(entry, savedFixed);
      }
    } else if (entry.scopeType === 'track') {
      const savedScoped = await saveTrackScope(entry);
      if (isPatch && !hasSyncFanout) {
        if (!applyPatchSavedScopedRowsToCaches(entry, 'track', savedScoped)) needsFullUiRefresh = true;
      } else {
        applyReplaceSavedScopedRowsToCaches(entry, 'track', savedScoped);
      }
    } else {
      const savedScoped = await saveScopedScope(entry);
      if (isPatch) {
        if (!applyPatchSavedScopedRowsToCaches(entry, entry.scopeType as ProgramScopeType, savedScoped)) {
          needsFullUiRefresh = true;
        }
      } else {
        applyReplaceSavedScopedRowsToCaches(entry, entry.scopeType as ProgramScopeType, savedScoped);
      }
    }
    const touchedKey = `${entry.workspaceId}|${entry.academicYear}|${entry.termNumber}|${entry.gradeId}`;
    touchedGradeTerms.set(touchedKey, {
      workspaceId: entry.workspaceId,
      academicYear: entry.academicYear,
      termNumber: entry.termNumber,
      gradeId: entry.gradeId,
    });
    removeProgramWorkspaceScopeEntry(entry.scopeId);
    savedCount += 1;
  }
  const uiRefreshMode: ProgramProgressUiRefreshMode = needsFullUiRefresh ? 'full' : 'cache';
  touchedGradeTerms.forEach((target) => {
    emitProgramProgressSaved({
      workspaceId: target.workspaceId,
      academicYear: target.academicYear,
      termNumber: target.termNumber,
      gradeId: target.gradeId,
      uiRefreshMode,
    });
  });
  await notifySchoolProgramMutation(workspaceId);
  return { savedCount, uiRefreshMode };
}

/** 지정한 스코프 ID 중 `isDirty`인 항목만 저장(페이지 단위 저장). 슬라이드 캔버스는 슬라이드 탭 저장 버튼으로만 DB 반영. */
export async function saveProgramWorkspaceScopes(scopeIds: string[]): Promise<number> {
  const want = new Set(scopeIds.map((id) => id.trim()).filter(Boolean));
  if (!want.size) return 0;
  const entries = getProgramWorkspaceScopeEntries()
    .filter((entry) => entry.isDirty && want.has(entry.scopeId))
    .sort((a, b) => a.updatedAt - b.updatedAt);
  const result = await persistProgramWorkspaceEntries(entries);
  return result.savedCount;
}

export async function saveProgramWorkspaceAll(excludeScopeId?: string): Promise<PersistProgramWorkspaceResult> {
  const entries = getProgramWorkspaceScopeEntries()
    .filter((entry) => entry.isDirty)
    .filter((entry) => entry.scopeId !== excludeScopeId)
    .sort((a, b) => a.updatedAt - b.updatedAt);
  return persistProgramWorkspaceEntries(entries);
}
